import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useCopyClones } from "../CopyCloneAI/useCopyClones";
import { useTemplateAutoSetup } from "./useTemplateAutoSetup";
import "./CopyTwinAutoSetupModal.css";

// =============================================================================
// COPYTWIN AUTO-SETUP MODAL
//
// Shown right after the user picks "CopyTwin Mode" for a brand-new template,
// while no info has been entered yet. It asks which CopyTwin to build the
// template from and offers a one-click auto-setup that derives the whole
// campaign brief (goal, pain point, what-makes-you-special), language and the
// twin structure from everything we already know about that clone.
//
// A small secondary link lets power users skip the derivation and set
// everything up manually.
//
// Returns control to the parent via onReady({ cloneId, setup }):
//   • setup = <object>  → auto-setup result, parent pre-fills the editor
//   • setup = null      → manual path, parent opens an (almost) empty editor
// =============================================================================

const ISO_TO_DISPLAY = {
  en: "English", de: "German", fr: "French", es: "Spanish", it: "Italian",
  pt: "Portuguese", nl: "Dutch", pl: "Polish", tr: "Turkish", sv: "Swedish",
  ja: "Japanese", zh: "Chinese",
};

const cloneInitial = (name) => {
  const n = (name || "").trim();
  return n ? n.charAt(0).toUpperCase() : "?";
};

const sampleCountOf = (clone) =>
  Array.isArray(clone?.voiceSamples?.fullEmails)
    ? clone.voiceSamples.fullEmails.length
    : 0;

// A clone is "ready" for a rich auto-setup once it has trained Voice DNA.
// Untrained clones can still be picked — the derivation just falls back to a
// minimal (structure + language) setup.
const cloneReadiness = (clone) => {
  if (clone?.voiceDNA) return { level: "ready", label: "Trained" };
  if (sampleCountOf(clone) > 0) return { level: "partial", label: "Samples added" };
  return { level: "empty", label: "Not trained" };
};

const CopyTwinAutoSetupModal = ({ open, userId, typeLabel, onCancel, onReady }) => {
  const { clones, loading } = useCopyClones(userId);
  const { deriveSetup, pending, error } = useTemplateAutoSetup(userId);
  const [selectedId, setSelectedId] = useState("");

  // Trained clones first, then by recency (useCopyClones already sorts by
  // createdAt desc — keep that as the secondary order).
  const sortedClones = useMemo(() => {
    const list = Array.isArray(clones) ? [...clones] : [];
    const rank = (c) => (c?.voiceDNA ? 0 : sampleCountOf(c) > 0 ? 1 : 2);
    return list.sort((a, b) => rank(a) - rank(b));
  }, [clones]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !pending) onCancel();
    };
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onCancel, pending]);

  if (!open) return null;

  const selectedClone = sortedClones.find((c) => c.id === selectedId) || null;
  const hasClones = sortedClones.length > 0;

  const handleAutoSetup = async () => {
    if (!selectedId || pending) return;
    const res = await deriveSetup({ cloneId: selectedId });
    // Even on a soft "skipped" result the backend hands back a deterministic
    // base setup — apply it so the user still lands in a pre-wired editor.
    if (res && res.setup) {
      onReady({ cloneId: selectedId, setup: res.setup });
    } else if (!error) {
      // No payload at all (network) — degrade to manual with the picked clone.
      onReady({ cloneId: selectedId, setup: null });
    }
  };

  const handleManual = () => {
    if (pending) return;
    onReady({ cloneId: selectedId || "", setup: null });
  };

  return createPortal(
    <div className="ctas-overlay bcScrim" onClick={pending ? undefined : onCancel}>
      <div
        className="ctas-modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="ctas-close"
          onClick={onCancel}
          disabled={pending}
          aria-label="Close"
        >
          <i className="fa-solid fa-xmark" />
        </button>

        <div className="ctas-header">
          <div className="ctas-eyebrow">
            <i className="fa-solid fa-wand-magic-sparkles" /> CopyTwin Auto-Setup
          </div>
          <h2 className="ctas-title">Build this {typeLabel || "template"} from a CopyTwin</h2>
          <p className="ctas-subtitle">
            Pick a CopyTwin and we&apos;ll set the whole template up for you — language,
            structure and the campaign brief (goal, pain point, what makes you
            special) are derived from everything your CopyTwin already knows.
          </p>
        </div>

        {/* Body */}
        {pending ? (
          <div className="ctas-loading">
            <div className="ctas-spinner" />
            <div className="ctas-loading-title">Analyzing your CopyTwin…</div>
            <div className="ctas-loading-sub">
              Reading recurring goals, pain points and your angle from the trained voice.
            </div>
          </div>
        ) : loading ? (
          <div className="ctas-loading">
            <div className="ctas-spinner" />
            <div className="ctas-loading-title">Loading your CopyTwins…</div>
          </div>
        ) : !hasClones ? (
          <div className="ctas-empty">
            <i className="fa-solid fa-robot ctas-empty-icon" />
            <div className="ctas-empty-title">No CopyTwin yet</div>
            <div className="ctas-empty-sub">
              Create and train a CopyTwin in CopyTwin AI first — then we can derive
              the whole template from it. You can still set this template up manually.
            </div>
          </div>
        ) : (
          <div className="ctas-clone-list" role="radiogroup" aria-label="Choose a CopyTwin">
            {sortedClones.map((clone) => {
              const readiness = cloneReadiness(clone);
              const isSelected = clone.id === selectedId;
              const lang =
                clone.language && clone.language !== "auto"
                  ? ISO_TO_DISPLAY[clone.language] || clone.language
                  : "Auto";
              return (
                <button
                  key={clone.id}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  className={`ctas-clone-card${isSelected ? " ctas-clone-card-selected" : ""}`}
                  onClick={() => setSelectedId(clone.id)}
                >
                  <div className="ctas-clone-avatar">
                    {clone.avatar ? (
                      <img src={clone.avatar} alt="" />
                    ) : (
                      <span>{cloneInitial(clone.name)}</span>
                    )}
                  </div>
                  <div className="ctas-clone-main">
                    <div className="ctas-clone-name">
                      {clone.name || "Untitled CopyTwin"}
                    </div>
                    <div className="ctas-clone-meta">
                      <span
                        className={`ctas-clone-badge ctas-clone-badge-${readiness.level}`}
                      >
                        <i className="fa-solid fa-circle" /> {readiness.label}
                      </span>
                      <span className="ctas-clone-metaitem">
                        <i className="fa-regular fa-envelope" /> {sampleCountOf(clone)} samples
                      </span>
                      <span className="ctas-clone-metaitem">
                        <i className="fa-solid fa-language" /> {lang}
                      </span>
                    </div>
                  </div>
                  <div className="ctas-clone-check" aria-hidden="true">
                    <i className="fa-solid fa-check" />
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {error && !pending && (
          <div className="ctas-error">
            <i className="fa-solid fa-triangle-exclamation" /> {error}
          </div>
        )}

        {selectedClone && cloneReadiness(selectedClone).level === "empty" && !pending && (
          <div className="ctas-hint">
            <i className="fa-solid fa-circle-info" /> This CopyTwin isn&apos;t trained yet —
            we&apos;ll set the structure &amp; language, but you&apos;ll fill the brief yourself.
          </div>
        )}

        {/* Footer actions */}
        {!pending && (
          <div className="ctas-footer">
            <button
              type="button"
              className="ctas-primary"
              onClick={handleAutoSetup}
              disabled={!selectedId}
            >
              <i className="fa-solid fa-wand-magic-sparkles" />
              Auto-setup with one click
            </button>
            <button type="button" className="ctas-manual-link" onClick={handleManual}>
              {hasClones ? "I'd rather set everything up manually" : "Set up manually instead"}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default CopyTwinAutoSetupModal;
